import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  fetchChartOfAccountLedger,
  type ChartLedger,
  type ChartOfAccountNode,
} from "@/lib/dataBank/chartOfAccountsStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

function money(amount: number, symbol: string, emptyZero = false): string {
  if (emptyZero && Math.abs(amount) < 0.005) return "—";
  const formatted = Math.abs(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol ? `${symbol} ` : ""}${formatted}`;
}

function moneySide(amount: number, side: "Dr" | "Cr", symbol: string): string {
  return `${money(amount, symbol)} ${side}.`;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

export function ChartOfAccountLedgerDialog({
  open,
  node,
  accountIds,
  currencySymbol,
  onOpenChange,
}: {
  open: boolean;
  node: ChartOfAccountNode | null;
  accountIds: number[];
  currencySymbol: string;
  onOpenChange: (open: boolean) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ledger, setLedger] = useState<ChartLedger | null>(null);

  useEffect(() => {
    if (!open || accountIds.length === 0) {
      setLedger(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchChartOfAccountLedger(accountIds)
      .then((data) => {
        if (!cancelled) setLedger(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isAxiosError(err) && err.response?.status === 403) {
          setError("You don’t have permission to view this ledger.");
          return;
        }
        setError("Could not load the account ledger.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, accountIds.join(",")]);

  const showAccountCol = (ledger?.accounts.length ?? 0) > 1;
  const title = node ? `${node.name}${node.code ? ` (${node.code})` : ""}` : "Account ledger";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-5xl flex-col gap-3 overflow-hidden">
        <DialogHeader>
          <DialogTitle className="pr-8">Ledger — {title}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Posted in / out with the other account (from / to) and a running balance.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm text-destructive">{error}</p>
        ) : !ledger || ledger.lines.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No posted ledger entries for this account.</p>
        ) : (
          <div className="-mx-1 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">Date</TableHead>
                  <TableHead>Voucher</TableHead>
                  {showAccountCol ? <TableHead>Account</TableHead> : null}
                  <TableHead>Particulars</TableHead>
                  <TableHead>From / To</TableHead>
                  <TableHead className="text-right">In</TableHead>
                  <TableHead className="text-right">Out</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledger.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(line.date)}</TableCell>
                    <TableCell className="font-mono text-xs">
                      <div>{line.voucher || "—"}</div>
                      {line.voucherType ? (
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{line.voucherType}</div>
                      ) : null}
                    </TableCell>
                    {showAccountCol ? (
                      <TableCell className="max-w-[9rem] truncate text-sm">{line.accountName || "—"}</TableCell>
                    ) : null}
                    <TableCell className="min-w-[12rem] max-w-[18rem]">
                      <div className="text-sm">{line.particulars || "—"}</div>
                      {line.reference ? (
                        <div className="text-xs text-muted-foreground">Ref: {line.reference}</div>
                      ) : null}
                    </TableCell>
                    <TableCell className="min-w-[8rem] max-w-[14rem] text-sm">{line.fromTo || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                      {money(line.in, currencySymbol, true)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-rose-700 dark:text-rose-400">
                      {money(line.out, currencySymbol, true)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {moneySide(line.balance, line.balanceSide, currencySymbol)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={showAccountCol ? 5 : 4} className="font-semibold">
                    Closing
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-semibold text-emerald-700 dark:text-emerald-400">
                    {money(ledger.totals.in, currencySymbol)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-semibold text-rose-700 dark:text-rose-400">
                    {money(ledger.totals.out, currencySymbol)}
                  </TableCell>
                  <TableCell
                    className={cn("text-right tabular-nums font-semibold", ledger.totals.balance < 0 && "text-rose-700")}
                  >
                    {moneySide(ledger.totals.balance, ledger.totals.balanceSide, currencySymbol)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
